import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Upload, X, FileImage, FileText, Loader2, XCircle, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useImageUpload, type UseImageUploadOptions } from '@/hooks/useImageUpload';
import { useImage } from '@/hooks/useImage';
import { formatFileSize, getMaxFileSize } from '@/config/storage';
import { useLingui } from '@lingui/react';
import { Trans, msg } from '@lingui/macro';
import { toast } from 'sonner';
import { images_GenerateUploadURLRequest } from '@/types/image';

// Lazy-load the PDF thumbnail so react-pdf/pdfjs (~300-400KB) is fetched only
// when a PDF preview actually renders — not on every form that mounts an upload.
const PdfThumbnail = React.lazy(() =>
  import('@/components/ui/pdf-thumbnail').then((m) => ({ default: m.PdfThumbnail })),
);

export interface ImageUploadProps extends Omit<UseImageUploadOptions, 'onSuccess' | 'onError'> {
  value?: string;
  onChange?: (imageId: string) => void;
  onRemove?: () => void;
  className?: string;
  disabled?: boolean;
  description?: string;
  showPreview?: boolean;
  previewUrl?: string;
  accept?: string;
  maxSize?: number;
  multiple?: boolean;
}

export function ImageUpload({
  value,
  onChange,
  onRemove,
  className,
  disabled = false,
  purpose,
  entityType,
  entityId,
  description,
  showPreview = true,
  previewUrl,
  accept,
  maxSize,
  multiple = false,
}: ImageUploadProps) {
  const { i18n } = useLingui();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(previewUrl || null);
  const [previewType, setPreviewType] = useState<'image' | 'pdf' | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  // Resolve existing image ID to get download URL and metadata
  const { data: existingImage } = useImage(value);

  // Cleanup blob URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (preview?.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const { uploadFile, uploadProgress, isUploading, reset, cancelUpload } = useImageUpload({
    purpose,
    entityType,
    entityId,
    onSuccess: (imageId) => {
      onChange?.(imageId);
    },
    onError: () => {
      reset();
      setPreview(null);
      setPdfFile(null);
      setUploadedFileName(null);
    },
  });

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    // Check if multiple files were selected in single-file mode
    if (!multiple && files.length > 1) {
      toast.info(i18n._(msg`imageUpload.multipleFilesInfo`));
    }

    const file = files[0];

    // Create preview for images and PDFs using blob URL
    if ((file.type.startsWith('image/') || file.type === 'application/pdf') && showPreview) {
      // Revoke previous blob URL if it exists
      if (preview?.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }

      const isPdf = file.type === 'application/pdf';
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
      setPreviewType(isPdf ? 'pdf' : 'image');
      setPdfFile(isPdf ? file : null);
      setUploadedFileName(null);
    } else {
      // For other non-image files, store the file name for display
      setPreview(null);
      setPreviewType(null);
      setPdfFile(null);
      setUploadedFileName(file.name);
    }

    // Upload file
    await uploadFile(file, description);
  }, [uploadFile, description, showPreview, preview, multiple, i18n]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !isUploading) {
      setIsDragging(true);
    }
  }, [disabled, isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (!disabled && !isUploading) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, [disabled, isUploading, handleFileSelect]);

  const handleClick = useCallback(() => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  }, [disabled, isUploading]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!disabled && !isUploading && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  }, [disabled, isUploading]);

  const handleRemove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    // Cleanup blob URL before removing
    if (preview?.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }

    setPreview(null);
    setPreviewType(null);
    setPdfFile(null);
    setUploadedFileName(null);
    reset();
    onRemove?.();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [reset, onRemove, preview]);

  const handleCancel = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    cancelUpload();

    // Cleanup blob URL if cancelling
    if (preview?.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }

    setPreview(null);
    setPreviewType(null);
    setPdfFile(null);
    setUploadedFileName(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [cancelUpload, preview]);

  const handleOpenFile = useCallback((e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const effectiveMaxSize = maxSize || getMaxFileSize(purpose);
  const effectiveAccept = accept || getAcceptString(purpose);

  // Determine what to show: local preview, local file name, or existing image/PDF from server
  const existingIsImage = existingImage?.contentType?.startsWith('image/');
  const existingIsPdf = existingImage?.contentType === 'application/pdf';
  const existingIsVisual = existingIsImage || existingIsPdf;
  const existingUrl = existingImage?.downloadUrl;
  const existingFilename = existingImage?.filename;

  // Show visual preview (image or PDF): local blob preview OR existing from server
  const showVisualPreview = (preview && showPreview) || (value && existingUrl && existingIsVisual && !preview && !uploadedFileName);
  const visualPreviewSrc = preview || (existingIsVisual ? existingUrl : undefined);
  const isVisualPreviewPdf = previewType === 'pdf' || (!preview && !uploadedFileName && existingIsPdf);

  // Show file preview: local uploaded file name OR existing non-visual file from server
  const showFilePreview = (!isUploading && uploadedFileName) || (value && existingUrl && !existingIsVisual && !preview && !uploadedFileName);
  const filePreviewName = uploadedFileName || existingFilename || 'Document';
  const filePreviewUrl = uploadedFileName ? undefined : existingUrl;

  return (
    <div className={cn('relative', className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept={effectiveAccept}
        multiple={multiple}
        onChange={(e) => handleFileSelect(e.target.files)}
        disabled={disabled || isUploading}
        className="sr-only"
      />

      <div
        className={cn(
          'relative overflow-hidden rounded-lg border-2 border-dashed transition-colors',
          'cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
          isDragging && 'border-primary bg-primary/5',
          !isDragging && 'border-muted-foreground/25 hover:border-muted-foreground/50',
          (disabled || isUploading) && 'cursor-not-allowed opacity-50',
          'min-h-[200px] flex items-center justify-center'
        )}
        role="button"
        tabIndex={disabled || isUploading ? -1 : 0}
        aria-label={i18n._(msg`imageUpload.dragAndDrop`)}
        aria-disabled={disabled || isUploading}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        {showVisualPreview && visualPreviewSrc ? (
          <div className="relative w-full h-full">
            {isVisualPreviewPdf ? (
              <React.Suspense
                fallback={
                  <div className="flex items-center justify-center w-full min-h-[200px] text-muted-foreground">
                    <FileText className="h-8 w-8 animate-pulse" />
                  </div>
                }
              >
                <PdfThumbnail url={pdfFile ?? visualPreviewSrc} width={400} className="w-full min-h-[200px] flex items-center justify-center" />
              </React.Suspense>
            ) : (
              <img
                src={visualPreviewSrc}
                alt={i18n._(msg`imageUpload.preview`)}
                className="w-full h-full object-contain"
              />
            )}
            {!isUploading && (
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 z-10"
                aria-label={i18n._({ id: 'imageUpload.remove', message: 'Remove' })}
                onClick={handleRemove}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : showFilePreview ? (
          <div className="relative w-full flex items-center gap-3 p-4">
            <FileText className="h-10 w-10 flex-shrink-0 text-muted-foreground" />
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-sm font-medium truncate">{filePreviewName}</span>
              {filePreviewUrl && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs text-primary hover:underline mt-0.5 w-fit"
                  onClick={(e) => handleOpenFile(e, filePreviewUrl)}
                >
                  <ExternalLink className="h-3 w-3" />
                  <Trans id="imageUpload.openFile">Open file</Trans>
                </button>
              )}
            </div>
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-6 w-6"
              aria-label={i18n._({ id: 'imageUpload.remove', message: 'Remove' })}
              onClick={handleRemove}
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="text-center p-6">
            {isUploading ? (
              <Loader2 className="mx-auto h-12 w-12 text-muted-foreground animate-spin" />
            ) : isDragging ? (
              <Upload className="mx-auto h-12 w-12 text-primary" />
            ) : (
              <FileImage className="mx-auto h-12 w-12 text-muted-foreground" />
            )}

            <div className="mt-4">
              {isUploading ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    {getUploadStatusText(uploadProgress.status, i18n)}
                  </p>
                  <Progress value={uploadProgress.progress} className="mt-2" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={handleCancel}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    <Trans id="imageUpload.cancelUpload">Cancel Upload</Trans>
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium">
                    <Trans id="imageUpload.clickToUpload">Click to upload or drag and drop</Trans>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {i18n._(
                      msg({
                        id: 'imageUpload.maxFileSize',
                        message: 'Maximum file size: {maxSize}',
                      }),
                      { maxSize: formatFileSize(effectiveMaxSize) }
                    )}
                  </p>
                </>
              )}
            </div>

            {uploadProgress.error && (
              <p className="text-sm text-destructive mt-2">
                {uploadProgress.error}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function getAcceptString(purpose: images_GenerateUploadURLRequest.purpose): string {
  switch (purpose) {
    case images_GenerateUploadURLRequest.purpose.AVATAR:
      return 'image/jpeg,image/jpg,image/png,image/gif,image/webp';
    case images_GenerateUploadURLRequest.purpose.DOCUMENT:
      return '.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv';
    case images_GenerateUploadURLRequest.purpose.ATTACHMENT:
      return 'image/*,.pdf,.txt';
    case images_GenerateUploadURLRequest.purpose.EXPORT:
      return '.json,.csv,.xls,.xlsx';
    default:
      return '*/*';
  }
}

function getUploadStatusText(
  status: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  i18n: { _: (message: any, values?: any) => string }
): string {
  switch (status) {
    case 'generating':
      return i18n._(msg`imageUpload.status.preparing`);
    case 'uploading':
      return i18n._(msg`imageUpload.status.uploading`);
    case 'confirming':
      return i18n._(msg`imageUpload.status.confirming`);
    case 'complete':
      return i18n._(msg`imageUpload.status.complete`);
    case 'cancelled':
      return i18n._(msg`imageUpload.status.cancelled`);
    default:
      return '';
  }
}
